import { z } from "zod";
import { SYMPTOM_CODES, type SymptomCode } from "./symptom-entry";

// TES-1254 (Ella 2.1 slice H) — "Besvärsguiden": one guide post per symptom
// in the register (SYMPTOM_REGISTER_V2). Drive "Ella 2.1 — Productdefinition,
// frågeregister, Besvärsguide och datakongruens" §6:
// "Besvärsguiden är den enda kanoniska kunskapsplatsen för patientinformation om besvär och biverkningar."
//
// The structure is fixed, Drive §6.2 "Gemensam sidstruktur":
//   summary            "Kort sammanfattning: vad besväret är."
//   why                "Varför det kan uppstå."
//   selfCare           "Vad patienten kan prova själv."
//   whenToContactElla  "När patienten bör kontakta Ella."
//   whenToSeekCare     "När patienten ska söka vård direkt. Säkerhetsinformation får inte döljas under Läs mer."
//   readMore           "Läs mer för frivillig fördjupning." (optional)
//                      TES-1498: one or two article keys, never prose.
// and the post ends with "Registrera och följ besväret", which opens the
// shared symptom module; that is the app's, not the post's.
//
// This is the minimal type the app renders. The twelve posts themselves are
// content (TES-1253): they arrive through the bulk import as
// `symptom_guide_post` entities (symptomGuidePostUpsertSchema). A QA
// placeholder (`placeholder`) is never real content and can never be
// published (a database check says so).

type Copy = { sv: string; en: string };

/** The sections of a post, in reading order. */
export const SYMPTOM_GUIDE_SECTIONS = [
  "summary",
  "why",
  "selfCare",
  "whenToContactElla",
  "whenToSeekCare",
  "readMore",
] as const;
export type SymptomGuideSection = (typeof SYMPTOM_GUIDE_SECTIONS)[number];

export const SYMPTOM_GUIDE_STATUSES = ["draft", "published"] as const;
export type SymptomGuideStatus = (typeof SYMPTOM_GUIDE_STATUSES)[number];

const sectionText = z.object({
  sv: z.string().trim().min(1),
  en: z.string(),
});

// TES-1498 (Ö-756) — "Läs mer" is one or two links to Kunskapsbank articles,
// by article key, never prose. Emelie 2026-10-20: "Säkerhetsinformation om
// när patienten ska kontakta Ella eller söka vård ska alltid vara direkt
// synlig i besvärstexten. Läs mer är frivillig fördjupning." A keyword filter
// over free text could not promise that; a section that holds no text can.
// Kunskapsbank v16 B01–B12 always give "Läs mer" as 1–2 article keys, e.g.
// "knowledge.nutrition.small_nourishing_meals".
export const SYMPTOM_GUIDE_READ_MORE_MAX = 2;
const ARTICLE_KEY = /^[a-z0-9]+(?:[._-][a-z0-9]+)*$/;

const readMoreArticleKeys = z
  .array(
    z
      .string()
      .regex(
        ARTICLE_KEY,
        "readMore holds article keys (e.g. knowledge.nutrition.small_nourishing_meals), never text",
      ),
  )
  .min(1)
  .max(SYMPTOM_GUIDE_READ_MORE_MAX)
  .refine((keys) => new Set(keys).size === keys.length, {
    message: "readMore names the same article twice",
  });

/**
 * One post as stored. Text is plain: paragraphs separated by a blank line,
 * and a line starting with "- " is a bullet.
 */
const symptomGuidePostShape = z.object({
  symptomCode: z.enum(SYMPTOM_CODES),
  summary: sectionText,
  why: sectionText,
  selfCare: sectionText,
  whenToContactElla: sectionText,
  whenToSeekCare: sectionText,
  readMore: readMoreArticleKeys.nullable(),
});

export const symptomGuidePostContentSchema = symptomGuidePostShape;
export type SymptomGuidePostContent = z.infer<
  typeof symptomGuidePostContentSchema
>;

/**
 * TES-1253 — one post as the bulk import (`symptom_guide_post`) carries it.
 * Identity is the symptom code: one post per symptom. `status` is stated, not
 * defaulted: approved content is imported as "published" (content is
 * pre-approved, CLAUDE.md), and a draft is a deliberate choice.
 */
export const symptomGuidePostUpsertSchema = symptomGuidePostShape.extend({
  status: z.enum(SYMPTOM_GUIDE_STATUSES),
});
export type SymptomGuidePostUpsert = z.infer<
  typeof symptomGuidePostUpsertSchema
>;

/** A row of the list ("Besvärsguiden"). */
export interface SymptomGuidePostSummary {
  symptomCode: SymptomCode;
  /** The register's name for the symptom (SYMPTOM_REGISTER_V2). */
  label: Copy;
  /** The post's summary, in her language (Swedish when not translated). */
  summary: string;
  /**
   * True for a post that is not published: served only where the
   * environment shows drafts (SYMPTOM_GUIDE_DRAFTS_VISIBLE, QA), and marked
   * as a draft in the app.
   */
  draft: boolean;
}

export interface SymptomGuideListView {
  /** In register order. Empty while no post is published. */
  posts: SymptomGuidePostSummary[];
}

/** A "Läs mer" link: an article key and its title in her language. */
export interface SymptomGuideReadMoreArticle {
  articleKey: string;
  title: string;
}

/** One post, in her language. */
export interface SymptomGuidePostView extends SymptomGuidePostSummary {
  why: string;
  selfCare: string;
  whenToContactElla: string;
  /** Always shown in full, never folded (Drive §6.2). */
  whenToSeekCare: string;
  /**
   * Always null since TES-1498: "Läs mer" holds no prose. Kept so app 2.0.11,
   * which folds this string under "Läs mer", still renders the post (it
   * hides the section when null).
   */
  readMore: null;
  /**
   * TES-1498 — "Läs mer": up to two published articles, in the post's order,
   * each opening the Article screen. An article that is not published is left
   * out; an empty list means no "Läs mer".
   */
  readMoreArticles: SymptomGuideReadMoreArticle[];
  /** ISO-8601. */
  updatedAt: string;
}
